import { z } from 'zod';
import { LedgerAccountStatus, LedgerActorType, Pence } from './money';

// Admin money tools (docs ECONOMY_LEDGER.md, sections 4 to 6): economy settings, money history, freeze,
// corrections, reversals and the two-admin approval queue.

const pence = z.number().int();
const reason = z.string().trim().min(5, 'Give a reason of at least 5 characters').max(500);

// the life-story situations a character starts from (Jerry, 9 October 2026: replaced the first three); each
// has its own starting money and its own first home (home.ts HOME_BY_SITUATION)
export const STARTING_SITUATIONS = {
  student: 'Student',
  young_worker: 'Young worker',
  new_arrival: 'New arrival',
} as const;
export type StartingSituation = keyof typeof STARTING_SITUATIONS;
export const SITUATION_HINTS: Record<StartingSituation, string> = {
  student: 'A room in a shared house, a student loan, low rent',
  young_worker: 'A studio flat of your own and some savings',
  new_arrival: 'A council flat, a small start-up grant, the cheapest rent',
};

const amount = z.number().int().min(0).max(100_000_000); // up to £1m for any one setting
export const EconomySettings = z
  .object({
    startingMoney: z.object({ student: amount, young_worker: amount, new_arrival: amount }).strict(),
    // transfers between players (rent, bills, gifts)
    newAccountHoldDays: z.number().int().min(0).max(90),
    perTransferLimitPence: amount,
    dailyTransferLimitPence: amount,
    transferReviewAbovePence: amount,
    // the fee on each transfer: a share (basis points, 100 = 1%) and a minimum
    transferFeeBps: z.number().int().min(0).max(2_000),
    transferFeeMinPence: amount,
    // corrections and reversals above this need a second admin; only the super admin may change it
    approvalThresholdPence: amount,
  })
  .strict();
export type EconomySettings = z.infer<typeof EconomySettings>;

// proposed starting values; the admin changes them from the dashboard (every change audited)
export const DEFAULT_ECONOMY_SETTINGS: EconomySettings = {
  startingMoney: { student: 60_000, young_worker: 150_000, new_arrival: 40_000 },
  newAccountHoldDays: 7,
  perTransferLimitPence: 50_000,
  dailyTransferLimitPence: 100_000,
  transferReviewAbovePence: 25_000,
  transferFeeBps: 100,
  transferFeeMinPence: 10,
  approvalThresholdPence: 100_000,
};
export const EconomySettingsReply = z.object({ settings: EconomySettings });
export type EconomySettingsReply = z.infer<typeof EconomySettingsReply>;

export const LedgerAccountView = z.object({
  id: z.string().uuid(),
  ownerType: z.enum(['system', 'life']),
  label: z.string(),
  ownerUserId: z.string().uuid().nullable(),
  lifeId: z.string().uuid().nullable(),
  kind: z.string(),
  status: LedgerAccountStatus,
  balancePence: pence,
});
export type LedgerAccountView = z.infer<typeof LedgerAccountView>;

export const LedgerEntryView = z.object({
  entryId: z.number().int(),
  transactionId: z.string().uuid(),
  kind: z.string(),
  amountPence: pence,
  balanceAfterPence: pence,
  actorType: LedgerActorType,
  reason: z.string().nullable(),
  reversesId: z.string().uuid().nullable(),
  createdAt: z.string(),
});
export type LedgerEntryView = z.infer<typeof LedgerEntryView>;
export const LedgerEntriesQuery = z.object({ before: z.coerce.number().int().positive().optional() }).strict();
export const LedgerEntriesReply = z.object({
  account: LedgerAccountView,
  entries: z.array(LedgerEntryView),
  nextBefore: z.number().int().nullable(),
});
export type LedgerEntriesReply = z.infer<typeof LedgerEntriesReply>;

export const LedgerTransactionView = z.object({
  id: z.string().uuid(),
  kind: z.string(),
  actorType: LedgerActorType,
  actorUserId: z.string().uuid().nullable(),
  reason: z.string().nullable(),
  reversesId: z.string().uuid().nullable(),
  reversedById: z.string().uuid().nullable(),
  createdAt: z.string(),
  // every side of the movement, so support sees where the money came from and went to
  entries: z.array(z.object({ account: LedgerAccountView, amountPence: pence, balanceAfterPence: pence })),
});
export type LedgerTransactionView = z.infer<typeof LedgerTransactionView>;
export const LedgerTransactionReply = z.object({ transaction: LedgerTransactionView });
export type LedgerTransactionReply = z.infer<typeof LedgerTransactionReply>;

// find a player's money by @username or email
export const PlayerMoneyQuery = z.object({ q: z.string().trim().min(3).max(120) }).strict();
export const PlayerMoneyReply = z.object({
  player: z.object({ id: z.string().uuid(), email: z.string().nullable(), username: z.string().nullable() }),
  accounts: z.array(LedgerAccountView),
});
export type PlayerMoneyReply = z.infer<typeof PlayerMoneyReply>;

export const FreezeRequest = z.object({ reason }).strict();
export const ReversalRequest = z.object({ reason }).strict();
export const AdjustmentRequest = z
  .object({ accountId: z.string().uuid(), direction: z.enum(['credit', 'debit']), amountPence: Pence, reason })
  .strict();
export type AdjustmentRequest = z.infer<typeof AdjustmentRequest>;

export const LedgerApprovalStatus = z.enum(['pending', 'approved', 'rejected']);
export const LedgerApproval = z.object({
  id: z.string().uuid(),
  action: z.enum(['adjustment', 'reversal']),
  amountPence: pence,
  accountId: z.string().uuid().nullable(),
  transactionId: z.string().uuid().nullable(),
  direction: z.enum(['credit', 'debit']).nullable(),
  reason: z.string(),
  status: LedgerApprovalStatus,
  requestedBy: z.object({ id: z.string().uuid(), email: z.string().nullable() }),
  decidedBy: z.object({ id: z.string().uuid(), email: z.string().nullable() }).nullable(),
  decisionNote: z.string().nullable(),
  resultTransactionId: z.string().uuid().nullable(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
});
export type LedgerApproval = z.infer<typeof LedgerApproval>;
export const LedgerApprovalsReply = z.object({ approvals: z.array(LedgerApproval) });
export type LedgerApprovalsReply = z.infer<typeof LedgerApprovalsReply>;
export const ApprovalDecision = z
  .object({ approve: z.boolean(), note: z.string().trim().max(500).optional() })
  .strict();
export type ApprovalDecision = z.infer<typeof ApprovalDecision>;

// a correction or reversal either happened at once, or waits for a second admin
export const MoneyActionReply = z.object({
  status: z.enum(['done', 'pending_approval']),
  transactionId: z.string().uuid().nullable(),
  approvalId: z.string().uuid().nullable(),
});
export type MoneyActionReply = z.infer<typeof MoneyActionReply>;

export const EconomySummaryReply = z.object({
  circulationPence: pence,
  accounts: z.number().int(),
  transactions: z.number().int(),
  pendingApprovals: z.number().int(),
  // transfers between players waiting for a check (optional: an older server does not send it)
  heldTransfers: z.number().int().optional(),
  systemAccounts: z.array(LedgerAccountView),
  check: z.object({ ok: z.boolean(), problems: z.array(z.string()) }),
});
export type EconomySummaryReply = z.infer<typeof EconomySummaryReply>;
